import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Dropdown, Space, Tag, Typography } from 'antd';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';

import GroupHeader from '../common/GroupHeader';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import ToggleRow from '../common/ToggleRow';
import UserRef from '../users/UserRef';
import { buildActionMenu } from '../common/actionMenu';
import { LinkShapeSummary } from './LinkShape';
import {
	ApproveRequestModal,
	DeclineRequestModal,
	LinkCollectionModal,
	RequestLinkModal,
} from './LinkModals';
import {
	getCollectionLinks,
	getLinkHistory,
	removeLink,
	turnLinkCopiesOff,
	updateLink,
	withdrawLinkRequest,
} from '../../services/collectionLinks';
import {
	COPIES_DESCRIPTION,
	COPIES_LABEL,
	KEYS_DESCRIPTION,
	KEYS_LABEL,
	COPY_FAILED_FIX_LABEL,
	describeCopyFailure,
	describeLinkError,
	describeLinkHistory,
	describeQueryWarnings,
	describeRequesterReach,
	endLinkConsequence,
	linkHistoryOtherName,
	linkSourceCandidates,
	linkTargetCandidates,
	sourceBlockedReason,
	targetBlockedReason,
} from '../../utils/collectionLinks';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { joinMeta } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { negatedTagWarningText } from '../../utils/tags';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useEnsureCards from '../../hooks/useEnsureCards';
import { invalidateLinkedSources } from '../../hooks/useLinkedSources';
import { apiErrorCode } from '../../utils/apiErrors';
import { KEBAB, PLUS } from '../icons';
import { confirmDestructive } from '../common/confirmDestructive';
import { useCollections } from '../../contexts/CollectionsContext';

const { Text } = Typography;

export const SECTION_ID = 'linked-collections';

/**
 * LINKED COLLECTIONS — the Sharing tab's section for collection links
 * (keepr-api docs/SCHEMA.md § Collection links).
 *
 * One collection can stand on either side of a link, so the section has two
 * halves, both on every collection:
 *
 *   Shared with other collections   this collection is A: what it shares,
 *                                   each link's two switches, Revoke, and
 *                                   "Link a collection…"
 *   Requests to share               others asking A to share: Approve (which
 *                                   may narrow) or Decline
 *   Read from other collections     this collection is B: what it reads,
 *                                   Remove, and "Request a link…"
 *   Your requests                   B's pending asks: Withdraw
 *   History                         every link that ended and every
 *                                   request that was answered, either way,
 *                                   newest first — who, what, from when to
 *                                   when (LinkHistory, below)
 *
 * Managers see all five. Everyone else with collection-level access
 * (`readOnly`) sees the third, read-only — what this collection reads from
 * elsewhere. Everything applies immediately and the view reloads from the
 * server after every change, like the rest of the tab.
 *
 * `vocabulary` is THIS collection's card options and filter fields (the
 * Sharing tab already builds them for grants): the approve dialog narrows a
 * request with them, the link dialog picks a shape with them.
 */
export default function LinkedCollectionsPanel({ collectionId, collection, readOnly = false, vocabulary = null }) {
	const { message, modal } = App.useApp();
	const navigate = useNavigate();
	const [view, setView] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(null);
	// The shared collections list; null until it has answered.
	const { collections: sharedCollections, available: collectionsLoaded } = useCollections();
	const collections = collectionsLoaded ? sharedCollections : null;
	const [dialog, setDialog] = useState(null); // 'link' | 'request' | { approve } | { decline }
	const [busyIds, setBusyIds] = useState(() => new Set());
	// Bumped by every reload: a change here (a revoke, an answer) is History's
	// newest entry, so History re-reads with the rest of the panel.
	const [historyKey, setHistoryKey] = useState(0);

	const defs = useOptionalCardDefinitions();
	const cardsById = useMemo(() => defs?.cardsById || {}, [defs?.cardsById]);
	const getElementsForCard = defs?.getElementsForCard;

	const reload = useCallback(() => {
		if (!collectionId) return Promise.resolve();
		return getCollectionLinks(collectionId)
			.then(next => { setView(next); setLoadError(null); setHistoryKey(key => key + 1); })
			.catch(error => setLoadError(error));
	}, [collectionId]);

	useEffect(() => {
		setLoading(true);
		reload().finally(() => setLoading(false));
	}, [reload]);

	// The caller's own collections: the pickers' candidates, and which of the
	// collections a link names they can open (a badge links only to those).
	const manages = !readOnly && view?.manages === true;

	// Scroll here when a notice deep-linked to the section.
	useEffect(() => {
		if (loading || typeof window === 'undefined' || window.location.hash !== `#${SECTION_ID}`) return;
		const node = document.getElementById(SECTION_ID);
		if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'start' });
	}, [loading]);

	const readableIds = useMemo(
		() => new Set((collections || []).map(c => normalizeId(c?._id)).filter(Boolean)),
		[collections]
	);

	// Cards of OTHER collections a link or request names — make sure the
	// cache holds them so the badge can say their names.
	const foreignCardIds = useMemo(() => [
		...(view?.incoming || []),
		...(view?.requestsSent || []),
	].map(row => normalizeId(row?.card_id)).filter(Boolean), [view]);
	useEnsureCards(foreignCardIds);

	// This collection's cards by id — its vocabulary first, then the cache —
	// for naming what a requester already sees here.
	const ownCardsById = useMemo(() => {
		const out = { ...cardsById };
		(vocabulary?.cardMetaById || new Map()).forEach((card, id) => { if (card) out[id] = { ...out[id], ...card }; });
		return out;
	}, [cardsById, vocabulary]);
	const ownCardMeta = useCallback(
		id => vocabulary?.cardMetaById?.get(normalizeId(id)) || cardsById[normalizeId(id)] || null,
		[vocabulary, cardsById]
	);
	const anyCardMeta = useCallback(id => cardsById[normalizeId(id)] || null, [cardsById]);
	// Chips for another collection's filter: every field the caller's cache
	// knows, which is enough to NAME a condition (the chips only read labels).
	const foreignFields = useMemo(
		() => buildFilterFields({ cards: Object.values(cardsById), cardIds: null, getElementsForCard }),
		[cardsById, getElementsForCard]
	);
	const ownFields = vocabulary?.filterFields || foreignFields;

	const setBusy = (id, on) => setBusyIds(prev => {
		const next = new Set(prev);
		if (on) next.add(id); else next.delete(id);
		return next;
	});

	/** Run a mutation, say what happened, and resync from the server either way. */
	const run = useCallback(async (id, mutation, { success, failure, onError = null }) => {
		setBusy(id, true);
		try {
			await mutation();
			if (success) message.success(success);
		} catch (error) {
			// A refusal with a way out (a failed keep-copy) is handled by the
			// caller; everything else is one sentence.
			if (!(onError && onError(error))) message.error(describeLinkError(error, failure));
		} finally {
			setBusy(id, false);
			// What this collection reads through links changed with it: lookup
			// pickers and the merged list re-ask.
			invalidateLinkedSources(collectionId);
			await reload();
		}
	}, [message, reload, collectionId]);

	const setSwitch = (link, field, value) => {
		const id = normalizeId(link._id);
		const apply = () => run(id, () => updateLink(collectionId, id, { [field]: value }), {
			failure: 'The switch could not be changed.',
		});
		// Turning copies ON is the one switch that cannot be taken back in
		// effect: what is copied while it is on stays copied.
		if (field === 'allowCopies' && value === true) {
			modal.confirm({
				title: `Let ${link.target?.name || 'the other collection'} keep copies?`,
				content: 'Its people may then export these records, download their attachments and copy them into their own collection — and anything they copy stays theirs, even if you turn this off or revoke the link later. Its managers are told.',
				okText: 'Allow copies',
				cancelText: 'Cancel',
				onOk: apply,
			});
			return;
		}
		apply();
	};

	const confirmEnd = (link, side) => {
		const id = normalizeId(link._id);
		const other = side === 'source' ? link.target?.name : link.source?.name;
		confirmDestructive(modal, {
			title: side === 'source'
				? `Revoke the link to ${other || 'this collection'}?`
				: `Remove the link from ${other || 'this collection'}?`,
			content: endLinkConsequence(link, side),
			okText: side === 'source' ? 'Revoke link' : 'Remove link',
			onOk: () => run(id, () => removeLink(collectionId, id), {
				success: side === 'source' ? 'Link revoked.' : 'Link removed.',
				failure: side === 'source' ? 'The link could not be revoked.' : 'The link could not be removed.',
				onError: error => {
					if (apiErrorCode(error) !== 'link_copy_failed') return false;
					explainCopyFailure(link, side);
					return true;
				},
			}),
		});
	};

	// A link filter that walks a lookup into another collection matches
	// nothing there (a stored query knows no link) — the API saves it and
	// says so in `warnings`; so does this. So does a filter that leaves items
	// out by a restricted tag (T7, `negated_restricted_tag`): the API's
	// sentence, as given.
	const warnHops = answer => {
		const text = [describeQueryWarnings(answer?.warnings, 'link filter'), negatedTagWarningText(answer?.warnings)].filter(Boolean).join(' ');
		if (text) message.warning(text);
	};

	// 409 `link_copy_failed`: the keep-copy the link promised could not be
	// made, so the link still stands. The way out always works — turn the
	// link's copies off (A's switch), and the next end is a tombstone. A's
	// managers get that as one button; B's are told whose switch it is.
	const explainCopyFailure = (link, side) => {
		const id = normalizeId(link._id);
		const words = describeCopyFailure(link, side);
		if (side === 'source') {
			confirmDestructive(modal, {
				title: words.title,
				content: words.body,
				okText: COPY_FAILED_FIX_LABEL,
				cancelText: 'Keep the link',
				onOk: () => run(id, async () => {
					await turnLinkCopiesOff(collectionId, id);
					await removeLink(collectionId, id);
				}, {
					success: 'Copies turned off and the link revoked.',
					failure: 'The link could not be revoked.',
				}),
			});
			return;
		}
		modal.error({ title: words.title, content: words.body, okText: 'OK' });
	};

	const confirmWithdraw = request => {
		const id = normalizeId(request._id);
		confirmDestructive(modal, {
			title: 'Withdraw this request?',
			content: `${request.source?.name || 'The other collection'}'s managers will no longer see it. You can ask again after a day.`,
			okText: 'Withdraw request',
			onOk: () => run(id, () => withdrawLinkRequest(collectionId, id), {
				success: 'Request withdrawn.',
				failure: 'The request could not be withdrawn.',
			}),
		});
	};

	const openCollection = id => (readableIds.has(normalizeId(id)) ? () => navigate(`/collections/${normalizeId(id)}`) : null);

	const retryLoad = () => {
		setLoading(true);
		reload().finally(() => setLoading(false));
	};

	const heading = (
		<>
			<h3>Linked collections</h3>
			<p className="linked-collections__intro">
				A link lets everyone who can see the whole of another collection read records from this one — read-only, and without the names of the people who wrote them.
			</p>
			{view && loadError ? <RefreshFailedNotice label="linked collections" onRetry={retryLoad} /> : null}
		</>
	);

	// Until the first read answers the panel is a skeleton, or the error with
	// Retry. Once it holds a view it keeps showing it: a failed reload is a
	// notice above the data, never a replacement for it (standards §14).
	if (!view && (loading || loadError)) {
		return (
			<section id={SECTION_ID} className="sharing-tab__section linked-collections">
				{heading}
				<TabLoadState
					loading={loading}
					error={loadError}
					label="linked collections"
					errorDetail={loadError ? describeLinkError(loadError, 'Check your connection and try again.') : undefined}
					onRetry={retryLoad}
					rows={3}
				/>
			</section>
		);
	}

	const outgoing = view?.outgoing || [];
	const incoming = view?.incoming || [];
	const toAnswer = view?.requestsToAnswer || [];
	const sent = view?.requestsSent || [];
	const sourceBlock = sourceBlockedReason(collection);
	const targetBlock = targetBlockedReason(collection);
	const targetCandidates = linkTargetCandidates(collections, collectionId);
	const sourceCandidates = linkSourceCandidates(collections, collectionId);

	const incomingSection = (
		<>
			<GroupHeader label="Read from other collections" count={incoming.length} />
			{incoming.length ? (
				<ul className="linked-collections__list">
					{incoming.map(link => (
						<LinkRow
							key={normalizeId(link._id)}
							badgeRecord={link.source}
							shape={<LinkShapeSummary row={link} cardMeta={anyCardMeta} filterFields={foreignFields} />}
							meta={joinMeta(
								link.allowCopies ? 'Copies allowed' : 'Read-only, no copies',
								link.createdAt ? `Linked ${formatDisplayDate(link.createdAt)}` : null
							)}
							actions={manages ? [
								openCollection(link.sourceId) ? { key: 'open', group: 'navigate', glyph: 'collection', label: 'Open collection', onClick: openCollection(link.sourceId) } : null,
								{ key: 'remove', group: 'destructive', glyph: 'remove', label: 'Remove link…', onClick: () => confirmEnd(link, 'target') },
							] : null}
							busy={busyIds.has(normalizeId(link._id))}
						/>
					))}
				</ul>
			) : (
				<Text type="secondary" className="linked-collections__empty">
					{manages && !targetBlock
						? 'This collection doesn\'t read from any other collection. Request a link to read another collection\'s records here.'
						: 'This collection doesn\'t read from any other collection.'}
				</Text>
			)}
		</>
	);

	if (!manages) {
		return (
			<section id={SECTION_ID} className="sharing-tab__section linked-collections">
				{heading}
				{incomingSection}
			</section>
		);
	}

	return (
		<section id={SECTION_ID} className="sharing-tab__section linked-collections">
			{heading}

			<GroupHeader label="Shared with other collections" count={outgoing.length} />
			{outgoing.length ? (
				<ul className="linked-collections__list">
					{outgoing.map(link => {
						const id = normalizeId(link._id);
						const busy = busyIds.has(id);
						return (
							<LinkRow
								key={id}
								badgeRecord={link.target}
								shape={<LinkShapeSummary row={link} cardMeta={ownCardMeta} filterFields={ownFields} />}
								meta={link.createdAt ? `Linked ${formatDisplayDate(link.createdAt)}` : null}
								actions={[
									openCollection(link.targetId) ? { key: 'open', group: 'navigate', glyph: 'collection', label: 'Open collection', onClick: openCollection(link.targetId) } : null,
									{ key: 'revoke', group: 'destructive', glyph: 'remove', label: 'Revoke link…', onClick: () => confirmEnd(link, 'source') },
								]}
								busy={busy}
							>
								<div className="linked-collections__switches">
									<ToggleRow
										first
										label={COPIES_LABEL}
										description={COPIES_DESCRIPTION}
										checked={link.allowCopies === true}
										disabled={busy}
										onChange={value => setSwitch(link, 'allowCopies', value)}
										aria-label={`${COPIES_LABEL} — ${link.target?.name || 'linked collection'}`}
									/>
									<ToggleRow
										label={KEYS_LABEL}
										description={KEYS_DESCRIPTION}
										checked={link.allowKeys !== false}
										disabled={busy}
										onChange={value => setSwitch(link, 'allowKeys', value)}
										aria-label={`${KEYS_LABEL} — ${link.target?.name || 'linked collection'}`}
									/>
								</div>
							</LinkRow>
						);
					})}
				</ul>
			) : (
				<Text type="secondary" className="linked-collections__empty">
					{sourceBlock || 'This collection isn\'t shared with any other collection yet.'}
				</Text>
			)}
			{sourceBlock ? null : (
				<div className="linked-collections__add">
					<Button icon={<PLUS />} onClick={() => setDialog('link')}>Link a collection…</Button>
				</div>
			)}

			{toAnswer.length ? (
				<>
					<GroupHeader label="Requests to share" count={toAnswer.length} />
					<ul className="linked-collections__list">
						{toAnswer.map(request => (
							<RequestToAnswerRow
								key={normalizeId(request._id)}
								request={request}
								cardMeta={ownCardMeta}
								cardsById={ownCardsById}
								filterFields={ownFields}
								busy={busyIds.has(normalizeId(request._id))}
								onApprove={() => setDialog({ approve: request })}
								onDecline={() => setDialog({ decline: request })}
							/>
						))}
					</ul>
				</>
			) : null}

			{incomingSection}
			{targetBlock ? (
				<Text type="secondary" className="linked-collections__empty">{targetBlock}</Text>
			) : (
				<div className="linked-collections__add">
					<Button icon={<PLUS />} onClick={() => setDialog('request')}>Request a link…</Button>
				</div>
			)}

			{sent.length ? (
				<>
					<GroupHeader label="Your requests" count={sent.length} />
					<ul className="linked-collections__list">
						{sent.map(request => (
							<LinkRow
								key={normalizeId(request._id)}
								badgeRecord={request.source}
								shape={<LinkShapeSummary row={request} cardMeta={anyCardMeta} filterFields={foreignFields} />}
								meta={joinMeta('Waiting for an answer', request.createdAt ? `Asked ${formatDisplayDate(request.createdAt)}` : null)}
								note={request.note}
								actions={[
									{ key: 'withdraw', group: 'destructive', glyph: 'remove', label: 'Withdraw request…', onClick: () => confirmWithdraw(request) },
								]}
								busy={busyIds.has(normalizeId(request._id))}
							/>
						))}
					</ul>
				</>
			) : null}

			<LinkHistory
				collectionId={collectionId}
				reloadKey={historyKey}
				ownCardMeta={ownCardMeta}
				anyCardMeta={anyCardMeta}
				ownFields={ownFields}
				foreignFields={foreignFields}
			/>

			<LinkCollectionModal
				open={dialog === 'link'}
				onClose={() => setDialog(null)}
				onDone={link => {
					setDialog(null);
					message.success(`Linked. People in ${link?.target?.name || 'that collection'} can now read what you shared.`);
					warnHops(link);
					invalidateLinkedSources();
					reload();
				}}
				sourceId={collectionId}
				candidates={targetCandidates}
				existing={outgoing}
				vocabulary={vocabulary}
			/>
			<RequestLinkModal
				open={dialog === 'request'}
				onClose={() => setDialog(null)}
				onDone={request => {
					setDialog(null);
					message.success(`Request sent. ${request?.source?.name || 'The other collection'}'s managers are told.`);
					warnHops(request);
					reload();
				}}
				targetId={collectionId}
				candidates={sourceCandidates}
			/>
			<ApproveRequestModal
				request={dialog?.approve || null}
				onClose={() => setDialog(null)}
				onDone={result => {
					setDialog(null);
					message.success('Approved. The link is live.');
					warnHops(result);
					invalidateLinkedSources();
					reload();
				}}
				sourceId={collectionId}
				vocabulary={vocabulary}
				cardsById={ownCardsById}
			/>
			<DeclineRequestModal
				request={dialog?.decline || null}
				onClose={() => setDialog(null)}
				onDone={() => {
					setDialog(null);
					message.success('Request declined.');
					reload();
				}}
				sourceId={collectionId}
			/>
		</section>
	);
}

/**
 * One link or request: the other collection's badge (a reference — the
 * record badge), what it covers and a meta line beneath, the ⋯ at the side,
 * and whatever the row carries below (the switches).
 */
function LinkRow({ badgeRecord, shape, meta, note = null, actions = null, busy = false, children = null }) {
	const name = badgeRecord?.name || 'A collection that no longer exists';
	const menu = actions ? buildActionMenu(actions.filter(Boolean)) : null;
	return (
		<li className={`linked-collections__row${busy ? ' linked-collections__row--busy' : ''}`} aria-busy={busy || undefined}>
			<RecordIdentity
				badge={<RecordBadge kind="collection" record={badgeRecord || null} title={name} size="sm" />}
				below={(
					<span className="linked-collections__below">
						{shape}
						{meta ? <span className="linked-collections__meta">{meta}</span> : null}
						{note ? <q className="linked-collections__quote">{note}</q> : null}
					</span>
				)}
				actions={menu ? (
					<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
						<Button icon={<KEBAB />} aria-label={`Actions for ${name}`} />
					</Dropdown>
				) : null}
			/>
			{children}
		</li>
	);
}

/**
 * A pending request to share THIS collection: who is asking, what for, who
 * asked and what they already see here, their note, and the two answers.
 */
function RequestToAnswerRow({ request, cardMeta, cardsById, filterFields, busy, onApprove, onDecline }) {
	const reach = describeRequesterReach(request.requesterReach, {
		cardsById,
		targetName: request.target?.name || 'their collection',
	});
	return (
		<li className={`linked-collections__row linked-collections__row--request${busy ? ' linked-collections__row--busy' : ''}`}>
			<RecordIdentity
				badge={<RecordBadge kind="collection" record={request.target || null} title={request.target?.name || 'A collection'} size="sm" />}
				below={(
					<span className="linked-collections__below">
						<span className="linked-collections__asked">
							<span>Asks for</span>
							<LinkShapeSummary row={request} cardMeta={cardMeta} filterFields={filterFields} />
						</span>
						<span className="linked-collections__requester">
							<Text type="secondary">Asked by</Text>
							<UserRef userId={request.requestedBy} size={20} />
							{request.createdAt ? <Text type="secondary">{`· ${formatDisplayDate(request.createdAt)}`}</Text> : null}
						</span>
					</span>
				)}
			/>
			{reach.sentence ? (
				<p className={`linked-collections__reach${reach.approvable ? '' : ' linked-collections__reach--blocked'}`}>{reach.sentence}</p>
			) : null}
			{request.note ? <blockquote className="linked-collections__note">{request.note}</blockquote> : null}
			<Space size={8} wrap className="linked-collections__answers">
				<Button onClick={onApprove} disabled={busy || !reach.approvable}>Approve…</Button>
				<Button onClick={onDecline} disabled={busy}>Decline…</Button>
			</Space>
		</li>
	);
}

const HISTORY_PAGE = 10;

/**
 * HISTORY — what this collection used to share and read: every link that
 * ended and every request that was answered, on either side of it, newest
 * first (keepr-api GET …/links/history, managers). The live pass asked for it
 * ("even after a link is revoked, the collection managers should be able to
 * view the history of what was linked to which collections and when they
 * started and ended"): a link row is deleted when it ends, and this is where
 * it is kept.
 *
 * Each row is the other collection's badge, what the link covered (or what
 * was asked for), when it ran, and who made and ended it. The other side's
 * people are never named here unless they were named to this side: where
 * they acted, the row says the other collection did (keepr-api D9). It
 * reloads with the panel (`reloadKey`), so a revoke shows here at once.
 */
function LinkHistory({ collectionId, reloadKey, ownCardMeta, anyCardMeta, ownFields, foreignFields }) {
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!collectionId) return undefined;
		let active = true;
		setLoading(true);
		getLinkHistory(collectionId, { limit: HISTORY_PAGE, skip: 0 })
			.then(page => {
				if (!active) return;
				setRows(page.rows);
				setTotal(page.total);
				setError(null);
			})
			.catch(e => { if (active) setError(e); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [collectionId, reloadKey, attempt]);

	// A card the history names by id only (the other collection's, on a
	// request that never opened it here) — named if the cache can hold it,
	// which it can only for someone who reads that card of their own.
	const unnamedCards = useMemo(
		() => rows.filter(e => e.card_id && !e.card && e.direction === 'incoming').map(e => normalizeId(e.card_id)),
		[rows]
	);
	useEnsureCards(unnamedCards);

	const more = async () => {
		setLoadingMore(true);
		try {
			const page = await getLinkHistory(collectionId, { limit: HISTORY_PAGE, skip: rows.length });
			setRows(prev => [...prev, ...page.rows]);
			setTotal(page.total);
		} catch (e) {
			setError(e);
		} finally {
			setLoadingMore(false);
		}
	};

	return (
		<div className="linked-collections__history">
			<GroupHeader label="History" count={total || undefined} />
			{!rows.length && (loading || error) ? (
				<TabLoadState
					loading={loading}
					error={error}
					label="link history"
					errorDetail={error ? describeLinkError(error, 'Check your connection and try again.') : undefined}
					onRetry={() => setAttempt(n => n + 1)}
					rows={2}
				/>
			) : null}
			{rows.length && error ? <RefreshFailedNotice label="link history" onRetry={() => setAttempt(n => n + 1)} /> : null}
			{!loading && !error && !rows.length ? (
				<Text type="secondary" className="linked-collections__empty">
					Links that end and requests that are answered are listed here, with who made them and when they ended.
				</Text>
			) : null}
			{rows.length ? (
				<ul className="linked-collections__list" aria-label="Link history">
					{rows.map(entry => (
						<LinkHistoryRow
							key={normalizeId(entry._id)}
							entry={entry}
							cardMeta={id => entry.card || (entry.direction === 'outgoing' ? ownCardMeta(id) : anyCardMeta(id))}
							filterFields={entry.direction === 'outgoing' ? ownFields : foreignFields}
						/>
					))}
				</ul>
			) : null}
			{rows.length && rows.length < total ? (
				<Button className="linked-collections__more" onClick={more} loading={loadingMore}>Show more</Button>
			) : null}
		</div>
	);
}

/** One ended link or answered request, in the sentences describeLinkHistory writes. */
function LinkHistoryRow({ entry, cardMeta, filterFields }) {
	const words = describeLinkHistory(entry);
	const other = entry.direction === 'outgoing' ? entry.target : entry.source;
	const name = linkHistoryOtherName(entry) || 'A collection that no longer exists';
	// `deleted` is all the API tells of a collection the caller can no longer
	// read (`current: false` — named as it was, keepr-api
	// collectionLinkHistory); `status` stays for an API a deploy behind.
	const deleted = !other || other.deleted === true || other.status === 'deleted';
	return (
		<li className="linked-collections__row linked-collections__row--history" data-event={entry.event}>
			<RecordIdentity
				badge={<RecordBadge kind="collection" record={other || null} title={name} size="sm" />}
				below={(
					<span className="linked-collections__below">
						<Tag className="linked-collections__status">{words.status}</Tag>
						{entry.kind === 'request' ? <span className="linked-collections__meta">Asked for</span> : null}
						<LinkShapeSummary row={entry} cardMeta={cardMeta} filterFields={filterFields} />
						<span className="linked-collections__meta">{joinMeta(words.when, words.copies, deleted ? 'Collection deleted' : null)}</span>
					</span>
				)}
			/>
			<span className="linked-collections__people">
				{entry.startedBy ? (
					<span className="linked-collections__requester">
						<Text type="secondary">{words.startedLabel}</Text>
						<UserRef userId={entry.startedBy} size={20} />
					</span>
				) : null}
				<span className="linked-collections__requester">
					<Text type="secondary">{words.endedLabel}</Text>
					{entry.endedBy ? <UserRef userId={entry.endedBy} size={20} /> : <Text type="secondary">{words.endedByText}</Text>}
				</span>
			</span>
			{entry.note ? <blockquote className="linked-collections__note">{entry.note}</blockquote> : null}
			{entry.decisionNote ? (
				<blockquote className="linked-collections__note">
					<Text type="secondary">Reply: </Text>{entry.decisionNote}
				</blockquote>
			) : null}
		</li>
	);
}
